import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PublicLayout } from '../components/layout/PublicLayout';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, AlertCircle, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const POLL_INTERVAL_MS = 4000;
const POLL_MAX_ATTEMPTS = 30; // 2 minutes

/**
 * PaymentSuccessPage
 * Paynow redirects back here after a card/web payment.
 * We poll the backend until the booking is confirmed, then navigate to the ticket.
 */
const PaymentSuccessPage = () => {
  const { bookingRef: routeBookingRef } = useParams();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);
  const paymentRefFromQuery = queryParams.get('m_payment_id') || queryParams.get('payment_id') || queryParams.get('reference') || queryParams.get('pf_payment_id');
  const bookingRef = routeBookingRef || paymentRefFromQuery || null;

  const [status, setStatus] = useState('polling'); // 'polling' | 'confirmed' | 'timeout' | 'error'
  const [attempts, setAttempts] = useState(0);
  const pollRef = useRef(null);
  const paymentIdRef = useRef(null);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    startPolling();
    return () => clearInterval(pollRef.current);
  }, [routeBookingRef]);

  const startPolling = async () => {
    try {
      let resolvedBookingRef = routeBookingRef || null;

      if (!resolvedBookingRef && paymentRefFromQuery) {
        const paymentRes = await axios.get(`${API_URL}/payments/reference/${encodeURIComponent(paymentRefFromQuery)}`);
        const payment = paymentRes.data;
        if (payment?.booking_ref) {
          resolvedBookingRef = payment.booking_ref;
        }
      }

      if (!resolvedBookingRef) {
        setStatus('error');
        return;
      }

      const bookingRes = await axios.get(`${API_URL}/bookings/${resolvedBookingRef}`);
      const booking = bookingRes.data;

      if (booking.payment_status === 'paid' || booking.status === 'confirmed') {
        setStatus('confirmed');
        toast.success('Payment confirmed!');
        setTimeout(() => navigate(`/ticket/${resolvedBookingRef}`), 1500);
        return;
      }

      const paymentsRes = await axios.get(`${API_URL}/payments/${booking.id}`);
      const payments = paymentsRes.data;
      const pending = payments
        .filter(p => p.status === 'pending' && (p.poll_url || p.redirect_url || p.payment_method === 'paystack'))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

      if (!pending) {
        setStatus('confirmed');
        setTimeout(() => navigate(`/ticket/${resolvedBookingRef}`), 1000);
        return;
      }

      paymentIdRef.current = pending.id;
      beginPolling(pending.id);
    } catch (err) {
      console.error('Error fetching booking for payment success:', err);
      setStatus('error');
    }
  };

  const beginPolling = (paymentId) => {
    let count = 0;
    pollRef.current = setInterval(async () => {
      count += 1;
      setAttempts(count);

      try {
        const res = await axios.get(`${API_URL}/payments/poll/${paymentId}`);
        if (res.data.paid) {
          clearInterval(pollRef.current);
          setStatus('confirmed');
          toast.success('Payment confirmed! 🎉');
          setTimeout(() => navigate(`/ticket/${bookingRef}`), 1800);
        } else if (count >= POLL_MAX_ATTEMPTS) {
          clearInterval(pollRef.current);
          setStatus('timeout');
        }
      } catch {
        // network blip — keep trying
      }
    }, POLL_INTERVAL_MS);
  };

  const progressPct = Math.min((attempts / POLL_MAX_ATTEMPTS) * 100, 100);

  /* ── Confirmed ── */
  if (status === 'confirmed') {
    return (
      <PublicLayout>
        <div className="max-w-lg mx-auto px-4 py-20 text-center">
          <CheckCircle2 className="h-24 w-24 text-green-500 mx-auto mb-6 animate-bounce" />
          <h1 className="font-heading text-3xl font-bold text-green-700 mb-2">
            Payment Successful!
          </h1>
          <p className="text-slate-500 mb-4">
            Your booking <span className="font-mono font-bold">{bookingRef}</span> is confirmed.
          </p>
          <Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" />
          <p className="text-xs text-slate-400 mt-2">Taking you to your ticket…</p>
        </div>
      </PublicLayout>
    );
  }

  /* ── Timeout ── */
  if (status === 'timeout') {
    return (
      <PublicLayout>
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <AlertCircle className="h-16 w-16 text-amber-400 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold mb-2">
            Payment Not Confirmed Yet
          </h1>
          <p className="text-slate-500 mb-6">
            We haven't received confirmation from Paynow yet.<br />
            If you completed payment, your ticket will be confirmed shortly.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button
              variant="outline"
              onClick={() => {
                setStatus('polling');
                setAttempts(0);
                if (paymentIdRef.current) beginPolling(paymentIdRef.current);
                else startPolling();
              }}
            >
              Check Again
            </Button>
            <Button
              className="btn-panashe"
              onClick={() => navigate(`/ticket/${bookingRef}`)}
            >
              View My Ticket
            </Button>
            <Button variant="outline" onClick={() => navigate(`/profile?ref=${encodeURIComponent(bookingRef || '')}`)}>
              My Dashboard
            </Button>
          </div>
        </div>
      </PublicLayout>
    );
  }

  /* ── Error ── */
  if (status === 'error') {
    return (
      <PublicLayout>
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold mb-2">Something Went Wrong</h1>
          <p className="text-slate-500 mb-6">
            We couldn't load your booking. Please check your ticket directly.
          </p>
          <Button className="btn-panashe" onClick={() => navigate(`/ticket/${bookingRef}`)}>
            View My Ticket
          </Button>
        </div>
      </PublicLayout>
    );
  }

  /* ── Polling / Waiting ── */
  return (
    <PublicLayout>
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <div className="relative mx-auto mb-6 w-20 h-20 flex items-center justify-center">
          <div className="absolute inset-0 rounded-full border-4 border-panashe-blue/20 animate-ping" />
          <Clock className="h-10 w-10 text-panashe-blue animate-pulse" />
        </div>

        <h1 className="font-heading text-2xl font-bold mb-2">Confirming Your Payment…</h1>
        <p className="text-slate-500 mb-6">
          We're checking with Paynow for booking{' '}
          <span className="font-mono font-bold">{bookingRef}</span>.
          <br />Please don't close this page.
        </p>

        <div className="w-full bg-slate-200 rounded-full h-2 mb-3">
          <div
            className="bg-panashe-blue h-2 rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <p className="text-xs text-slate-400 mb-8">
          {Math.round(progressPct)}% — checking every 4 seconds
        </p>

        <Button
          variant="outline"
          className="text-sm"
          onClick={() => navigate(`/ticket/${bookingRef}`)}
        >
          Skip &amp; View Ticket
        </Button>
        <Button variant="outline" className="ml-2 text-sm" onClick={() => navigate(`/profile?ref=${encodeURIComponent(bookingRef || '')}`)}>
          My Dashboard
        </Button>
      </div>
    </PublicLayout>
  );
};

export default PaymentSuccessPage;
